import { supabase } from '@/services/supabase/client';
import type {
  Industry,
  BusinessFunction,
  BusinessRole,
  BusinessGoal,
  Capability,
  AiCapability,
} from '@/types';

export async function getIndustries() {
  const { data, error } = await supabase
    .from('industries')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return data as Industry[];
}

export async function getIndustryBySlug(slug: string) {
  const { data, error } = await supabase
    .from('industries')
    .select('*')
    .eq('slug', slug)
    .eq('is_active', true)
    .maybeSingle();

  if (error) throw error;
  return data as Industry | null;
}

export async function getBusinessFunctions() {
  const { data, error } = await supabase
    .from('business_functions')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return data as BusinessFunction[];
}

export async function getBusinessRoles(functionId?: string) {
  let query = supabase
    .from('business_roles')
    .select('*')
    .eq('is_active', true);

  // Include enterprise C-suite roles (function_id null) alongside function-specific roles
  if (functionId) {
    query = query.or(`function_id.eq.${functionId},function_id.is.null`);
  }

  const { data, error } = await query.order('name');
  if (error) throw error;
  return data as BusinessRole[];
}

export async function getBusinessProblems() {
  const { data, error } = await supabase
    .from('business_problems')
    .select('id, slug, title, short_description, status')
    .eq('status', 'published')
    .order('title');

  if (error) throw error;
  return data as Array<{
    id: string;
    slug: string;
    title: string;
    short_description: string | null;
    status: string;
  }>;
}

/** Pick problem / function / role from industry + goal taxonomy links. */
export async function resolveOntologyFromIndustryGoal(
  industryId: string,
  goalId: string,
): Promise<{
  business_problem_id: string;
  function_id: string;
  role_id: string;
  problem_title: string;
  function_name: string;
  role_name: string;
} | null> {
  const { data: goalLinks, error: goalErr } = await supabase
    .from('business_problem_goals')
    .select('business_problem_id')
    .eq('goal_id', goalId);

  if (goalErr) throw goalErr;

  let problemIds = (goalLinks ?? []).map((l) => l.business_problem_id as string);

  if (problemIds.length === 0) {
    const { data: fallback, error } = await supabase
      .from('business_problems')
      .select('id')
      .eq('status', 'published')
      .order('title')
      .limit(5);
    if (error) throw error;
    problemIds = (fallback ?? []).map((p) => p.id);
  }

  if (problemIds.length === 0) return null;

  const { data: industryLinks } = await supabase
    .from('business_problem_industries')
    .select('business_problem_id')
    .eq('industry_id', industryId)
    .in('business_problem_id', problemIds);

  const preferred = (industryLinks ?? []).map((l) => l.business_problem_id as string);
  const problemId = preferred[0] ?? problemIds[0];

  const { data: problem, error: problemErr } = await supabase
    .from('business_problems')
    .select('id, title')
    .eq('id', problemId)
    .maybeSingle();

  if (problemErr) throw problemErr;
  if (!problem) return null;

  const { data: fnLink } = await supabase
    .from('business_problem_functions')
    .select('function_id')
    .eq('business_problem_id', problemId)
    .limit(1)
    .maybeSingle();

  let functionId = (fnLink?.function_id as string) || '';
  let functionName = '';

  if (functionId) {
    const { data: fn } = await supabase
      .from('business_functions')
      .select('id, name')
      .eq('id', functionId)
      .maybeSingle();
    functionName = fn?.name ?? '';
  } else {
    const { data: fn } = await supabase
      .from('business_functions')
      .select('id, name')
      .eq('is_active', true)
      .order('sort_order')
      .limit(1)
      .maybeSingle();
    functionId = fn?.id ?? '';
    functionName = fn?.name ?? '';
  }

  const { data: roleLink } = await supabase
    .from('business_problem_roles')
    .select('role_id')
    .eq('business_problem_id', problemId)
    .limit(1)
    .maybeSingle();

  let roleId = (roleLink?.role_id as string) || '';
  let roleName = '';

  if (roleId) {
    const { data: role } = await supabase
      .from('business_roles')
      .select('id, name')
      .eq('id', roleId)
      .maybeSingle();
    roleName = role?.name ?? '';
  } else if (functionId) {
    const { data: role } = await supabase
      .from('business_roles')
      .select('id, name')
      .or(`function_id.eq.${functionId},function_id.is.null`)
      .order('name')
      .limit(1)
      .maybeSingle();
    roleId = role?.id ?? '';
    roleName = role?.name ?? '';
  }

  return {
    business_problem_id: problem.id,
    function_id: functionId,
    role_id: roleId,
    problem_title: problem.title,
    function_name: functionName,
    role_name: roleName,
  };
}

export async function getBusinessGoals() {
  const { data, error } = await supabase
    .from('business_goals')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return data as BusinessGoal[];
}

export async function getBusinessGoalBySlug(slug: string) {
  const { data, error } = await supabase
    .from('business_goals')
    .select('*')
    .eq('slug', slug)
    .eq('is_active', true)
    .maybeSingle();

  if (error) throw error;
  return data as BusinessGoal | null;
}

export async function getCapabilities() {
  const { data, error } = await supabase
    .from('capabilities')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return data as Capability[];
}

export async function getAiCapabilities() {
  const { data, error } = await supabase
    .from('ai_capabilities')
    .select('*')
    .eq('is_active', true)
    .order('name');

  if (error) throw error;
  return data as AiCapability[];
}
